import { Stack } from "@mui/material";
import { AlertsContext } from "@sista/dista-ui";
import {
  normalizePhoneNumber,
  Profile,
  Right,
  SistaDialog,
  SistaFormHeader,
  SmartButton,
  UiContext,
  useEmailValidation,
  useMaxLengthValidation,
  useMinLengthValidation,
  useNameValidation,
  usePhoneNumberValidation,
  useRequiredValidation,
  useSafeSistaAsync,
} from "@sista/library-stasi";
import { useCallback, useContext } from "react";
import { useForm } from "react-hook-form";
import { FormContainer, TextFieldElement } from "react-hook-form-mui";
import { useTranslation } from "react-i18next";

import { useRejstrikClient } from "../../../api/utils/useRejstrikClient";

export type DepartmentEditForm = Profile & {
  section?: string;
  info?: string;
  tempDate?: unknown;
};

export type DepartmentEditDialogProps = {
  departmentId: string;
  profile: Profile;
  onDone: () => unknown;
};

export const DepartmentEditDialog = (props: DepartmentEditDialogProps) => {
  const { closeDialogs } = useContext(UiContext);
  const alerts = useContext(AlertsContext);
  const departmentGenerichKey = props.departmentId.replace(":", ".");
  const departmentGenerichCode = departmentGenerichKey.split(".")[1];

  const storedSection = (props.profile as any)._viewData?.d[
    departmentGenerichCode
  ]?.details?.section;
  const storedInfo = (props.profile as any)._viewData?.d[departmentGenerichCode]
    ?.details?.info;

  const form = useForm<DepartmentEditForm, unknown>({
    defaultValues: {
      ...props.profile,
      section: storedSection,
      info: storedInfo,
    },
  });
  const { t } = useTranslation("ucet");

  const phone = usePhoneNumberValidation();
  const email = useEmailValidation();
  const maxLength = useMaxLengthValidation(37);
  const maxInfoLength = useMaxLengthValidation(1000);
  const minLength = useMinLengthValidation(2);
  const required = useRequiredValidation();
  //  const reasonableDate = useReasonableDateValidation();
  //  const email = useEmailValidation();
  const nameValidation = useNameValidation();

  const { updateProfileInDepartment, setCustomDataToProfileInDepartment } =
    useRejstrikClient();

  const updateProfileImpl = useCallback(
    async (formData: DepartmentEditForm) => {
      formData.phone = normalizePhoneNumber(formData.phone);
      await updateProfileInDepartment({
        departmentId: props.departmentId,
        profileId: props.profile.id,
        changeReason: "update from /ucet",
        minorChange: false,
        data: formData,
      });
      const details = {
        ...((props.profile as any)._viewData.d[departmentGenerichKey]
          ?.details ?? {}),
        section: formData.section,
        info: formData.info,
      };
      if (formData.section != storedSection || formData.info != storedInfo) {
        await setCustomDataToProfileInDepartment({
          departmentId: props.departmentId,
          profileId: props.profile.id,
          generichKey: props.departmentId.replace(":", ".") + ".details",
          changeReason: "update from /ucet",
          minorChange: false,
          data: details,
        }).catch((e: unknown) => {
          console.error(e);
          alerts.showError(
            t(
              "ucet.department.errorNoDetails",
              "Detailnější informace se nepodařilo uložit, doplňte je prosím později."
            )
          );
        });
      }
      alerts.showSuccess(
        t("ucet.department.profileUpdated", "Profil byl upraven")
      );
      props.onDone();
      closeDialogs();
    },
    [
      alerts,
      closeDialogs,
      departmentGenerichKey,
      props,
      setCustomDataToProfileInDepartment,
      storedSection,
      t,
      updateProfileInDepartment,
      storedInfo,
    ]
  );

  const [updateProfileCall, updateProfileState] =
    useSafeSistaAsync(updateProfileImpl);

  return (
    <SistaDialog
      title={t("ucet.department.editDialogTitle", "Změna vybraných údajů")}
      onClose={closeDialogs}
    >
      <SistaFormHeader
        title={t("ucet.department.editTitle", "Změna profilu")}
        subtitle={t(
          "ucet.department.editSubtitle",
          "O případné další změny požádejte administrátora SISTA."
        )}
      />

      <FormContainer onSuccess={updateProfileCall} formContext={form}>
        <Stack direction="column" spacing={2}>
          <TextFieldElement
            name="section"
            autoComplete={"off"}
            label={t("ucet.department.form.section", "Útvar")}
            validation={{
              ...maxLength,
              ...minLength,
            }}
          />

          <TextFieldElement
            name="info"
            autoComplete={"off"}
            label={t("ucet.department.form.info", "Doplňující informace")}
            validation={{
              ...maxInfoLength,
            }}
          />

          <TextFieldElement
            name="phone"
            placeholder={t(
              "ucet.department.form.phonePlaceholder",
              "999 999 999"
            )}
            autoComplete={"off"}
            label={t("ucet.department.form.phone", "Kontaktní telefon")}
            validation={{
              ...phone,
            }}
          />

          <TextFieldElement
            name="email"
            autoComplete={"off"}
            label={t("ucet.department.form.email", "Kontaktní email")}
            validation={{
              ...email,
              ...required,
            }}
          />

          <TextFieldElement
            name="titlesBefore"
            autoComplete={"off"}
            label={t("ucet.department.form.titlesBefore", "Tituly před jménem")}
            validation={{}}
          />

          <TextFieldElement
            name="titlesAfter"
            autoComplete={"off"}
            label={t("ucet.department.form.titlesAfter", "Tituly za jménem")}
            validation={{}}
          />

          <Right sx={{ marginTop: 4 }}>
            <SmartButton state={updateProfileState}>
              {t("ucet.department.buttonEdit", "Uložit změny")}
            </SmartButton>
          </Right>
        </Stack>
      </FormContainer>
    </SistaDialog>
  );
};
